import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { delay, Observable, of, switchMap, throwError } from 'rxjs';
import { DocumentTemplateManagementConstants } from '../document-template-management-constants';
import { DocumentTemplate } from '../models/document-template';
import { DocumentTemplateManagementService } from '../services/document-template-management.service';
import { DocumentTemplateDetails } from '../models/document-template-details';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DocumentTemplateManagementInteractionService } from '../services/document-template-management-interaction.service';
import { SharedConstants } from '../../../app/shared/constants/shared-constants';

@Component({
  selector: 'app-document-template-slider',
  templateUrl: './document-template-slider.component.html',
  styleUrls: ['./document-template-slider.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DocumentTemplateSliderComponent implements OnInit {
  @Input() documentTemplateDetail: DocumentTemplateDetails | undefined;
  @Input() isDocumentTemplateSliderOpen: boolean = false;
  @Output() isDocumentTemplateSliderActive = new EventEmitter<boolean>();
  documentTemplateHeaderText: string = DocumentTemplateManagementConstants.Add_Document_Template_Header_Text;
  maxFileSize = DocumentTemplateManagementConstants.Document_Template_File_Max_Size;
  maxFileLimit = DocumentTemplateManagementConstants.Document_Template_File_Max_Limit;
  documentTemplateFileTypes = DocumentTemplateManagementConstants.Document_Template_File_Types;
  documentTemplateIsSaveDisable: boolean = true;
  documentTemplateName: string = "";
  savedFiles: string[] = [];
  uploadedFiles: File[] = [];
  errorMessages: string[] = [];
  isDocumentTemplateNameValid: boolean = true;
  disableFileUploader: boolean = false;
  isDocumentTemplateSaveInProgress: boolean = false;
  errorMessageHeaderText: string = DocumentTemplateManagementConstants.Document_Template_Error_Message_Header_Text;
  documentTemplateSaveProgressMessage: string = DocumentTemplateManagementConstants.Document_Template_Save_Progress_Message;
  isEditDocumentTemplate: boolean = false;
  allowMultipleFiles:boolean=false;
  constructor(private documentTemplateManagementService: DocumentTemplateManagementService,
    private documentTemplateManagementInteractionService: DocumentTemplateManagementInteractionService,
    private confirmationService: ConfirmationService, private messageService: MessageService) { }

  ngOnInit(): void {
    if (!!this.documentTemplateDetail
      && this.documentTemplateDetail.fileId.length > 0) {
      this.setDocumentTemplateForEdit();
    }
  }
  setDocumentTemplateForEdit = () => {
    this.documentTemplateHeaderText = DocumentTemplateManagementConstants.Edit_Document_Template_Header_Text;
    this.documentTemplateSaveProgressMessage = DocumentTemplateManagementConstants.Document_Template_Update_Progress_Message;
    this.disableFileUploader = true;
    this.documentTemplateName = this.documentTemplateDetail?.name || '';
    this.savedFiles.push(this.documentTemplateDetail?.fileName || '');
    this.isEditDocumentTemplate = true;
    this.documentTemplateIsSaveDisable = false;
  }
  uploadSucceeds = (files: File[]): Observable<string[]> => {
    if (this.uploadedFiles.length === 0) {
      this.disableFileUploader = true;
      this.uploadedFiles = files;
      if (this.isEditDocumentTemplate) {
        this.errorMessages = this.errorMessages.filter((msg) => {
          return msg !== DocumentTemplateManagementConstants.Document_Template_File_Required_Error_Message_Text;
        });
      }
    }
    const fileNames = files.map((i) => i.name);
    this.enableOrDisableSaveButton();
    return of(fileNames).pipe(delay(3000));
  };

  deleteFile = (): Observable<boolean> => {
    this.savedFiles = [];
    this.uploadedFiles = [];
    this.disableFileUploader = false;
    this.enableOrDisableSaveButton();
    if (this.isEditDocumentTemplate &&
      this.errorMessages.indexOf(DocumentTemplateManagementConstants.Document_Template_File_Required_Error_Message_Text) === -1) {
      this.errorMessages.push(DocumentTemplateManagementConstants.Document_Template_File_Required_Error_Message_Text);
    }
    return of(true);
  };
  saveDocumentTemplate = () => {
    this.isDocumentTemplateSaveInProgress = true;
    this.validateDocumentTemplateName();
  }
  enableOrDisableSaveButton = () => {
    this.documentTemplateIsSaveDisable = this.documentTemplateName.trim().length > 0
      && (this.uploadedFiles.length > 0 || this.savedFiles.length > 0) ? false : true;
  }
  openConfirmationModal = () => {
    this.confirmationService.confirm({
      header: DocumentTemplateManagementConstants.Document_Template_Management_Confirmation_Header,
      message: DocumentTemplateManagementConstants.Document_Template_Slider_Confirmation_Message,
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeSlider();
      },
    });
  }
  private validateDocumentTemplateName = (): void => {
    this.isDocumentTemplateNameValid = true;
    this.errorMessages = [];
    let documentTemplateSavedName = this.isEditDocumentTemplate ? this.documentTemplateDetail?.name || '' : '';
    
    this.documentTemplateManagementService.checkDocumentTemplateNameIsValid(this.documentTemplateName, documentTemplateSavedName)
      .subscribe({
        next: (isExists) => {
          if (isExists) {
            this.isDocumentTemplateNameValid = false;
            this.errorMessages.push(DocumentTemplateManagementConstants.Document_Template_Unique_Error_Message_Text);
            this.isDocumentTemplateSaveInProgress = false;
          } else {
            this.isEditDocumentTemplate ? this.updateDocumentTemplate() : this.createDocumentTemplate();
          }
        },
        error: (error) => {
          this.showToasterMessage(DocumentTemplateManagementConstants.Severity_Err,
            DocumentTemplateManagementConstants.Document_Template_Upload_Failed);
            this.isDocumentTemplateSaveInProgress = false;
        }
      });
  }
  
  
private createDocumentTemplate = (): void => {
  const fileName = this.uploadedFiles[0]?.name || 'Unknown file';
  
  this.documentTemplateManagementService.uploadDocumentTemplate(
    this.uploadedFiles[0], 
    DocumentTemplateManagementConstants.Document_Templates_Upload_Folder
  )
    .subscribe({
      next: (response) => {
        this.documentTemplateManagementService.saveDocumentTemplate(
          this.constructDocumentTemplate(this.uploadedFiles[0], response)
        )
          .subscribe({
            next: (templateResponse) => {
              this.loadDocumentTemplates();
              this.isDocumentTemplateSaveInProgress = false;
            },
            error: (err) => {
              this.showToasterMessage(
                DocumentTemplateManagementConstants.Severity_Err,
                `Failed to save document template: ${fileName}`,
                err|| SharedConstants.Document_Template_Upload_Error
              );
              this.isDocumentTemplateSaveInProgress = false;
            }
          });
      },
      error: (err) => {
        this.showToasterMessage(
          DocumentTemplateManagementConstants.Severity_Err,
          `Failed to upload document template: ${fileName}`,
          err|| SharedConstants.Document_Template_Upload_Error
        );
        this.isDocumentTemplateSaveInProgress = false;
      }
    });
}

  private updateDocumentTemplate = (): void => {
    if (this.savedFiles.length === 0) {
      this.replaceDocumentTemplateFile();
    } else {
      this.documentTemplateManagementService.updateDocumentTemplate(this.constructDocumentTemplateForUpdate(this.uploadedFiles[0], {}, false))
        .subscribe({
          next: (templateResponse) => {
            this.loadDocumentTemplates();
          },
          error: (error) => {
            this.showToasterMessage(DocumentTemplateManagementConstants.Severity_Err,
              DocumentTemplateManagementConstants.Document_Template_Upload_Failed);
              this.isDocumentTemplateSaveInProgress = false;
          }
        });
    }
  }

  private replaceDocumentTemplateFile = (): void => {
    const existingFileId = this.documentTemplateDetail?.fileId || '';
    const replacementFile = this.uploadedFiles[0];

    this.documentTemplateManagementService.deleteFile(existingFileId)
      .pipe(
        switchMap((deletedResponse) => {
          if (!deletedResponse) {
            return throwError(() => new Error('Unable to delete existing template file.'));
          }

          return this.documentTemplateManagementService.uploadDocumentTemplate(
            replacementFile,
            DocumentTemplateManagementConstants.Document_Templates_Upload_Folder
          );
        }),
        switchMap((response) => {
          return this.documentTemplateManagementService.updateDocumentTemplate(
            this.constructDocumentTemplateForUpdate(replacementFile, response, true)
          );
        })
      )
      .subscribe({
        next: () => {
          this.loadDocumentTemplates();
        },
        error: () => {
          this.showToasterMessage(
            DocumentTemplateManagementConstants.Severity_Err,
            DocumentTemplateManagementConstants.Document_Template_Upload_Failed
          );
          this.isDocumentTemplateSaveInProgress = false;
        }
      });
  }
  
  private loadDocumentTemplates = (): void => {
    this.closeSlider();
    this.documentTemplateManagementInteractionService.removeScopeSelection();
    this.isDocumentTemplateSaveInProgress = false;
    this.showToasterMessage(DocumentTemplateManagementConstants.Severity_Success,
      this.isEditDocumentTemplate ? DocumentTemplateManagementConstants.Document_Template_Updated_Success :
        DocumentTemplateManagementConstants.Document_Template_Created_Success, undefined, false);
    setTimeout(() => {
      this.documentTemplateManagementInteractionService.refreshDocumentTemplates();
    }, 2000);
  }
 private showToasterMessage = (severityMessage: string, summaryMessage: string, detailMessage?: string, isSticky = true): void => {
  this.messageService.add({
    severity: severityMessage,
    summary: summaryMessage,
    detail: detailMessage,
    sticky: isSticky
  });
}

  private closeSlider = (): void => {
    this.resetDocumentTemplate();
    this.isDocumentTemplateSliderActive.emit(false);
  }
  private constructDocumentTemplate = (file: File, fileSaveResponse: any): DocumentTemplate => {
    return {
      fileId: fileSaveResponse.fileId,
      name: this.documentTemplateName,
      fileName: file.name,
      fileType: fileSaveResponse.fileExtension,
      filePath: fileSaveResponse.filePath,
      createdBy: '',
      createdDate: new Date(),
      isActive: true
    }
  }
  private constructDocumentTemplateForUpdate = (file: File,
    fileSaveResponse: any, isFileUpdated: boolean): DocumentTemplate => {
    return {
      templateId: this.documentTemplateDetail?.documentTemplateId,
      fileId: fileSaveResponse.fileId,
      name: this.documentTemplateName,
      fileName: isFileUpdated ? file.name : '',
      fileType: isFileUpdated ? fileSaveResponse.fileExtension : '',
      filePath: isFileUpdated ? fileSaveResponse.filePath : '',
      createdBy: '',
      createdDate: new Date(),
      isActive: true
    }
  }
  private resetDocumentTemplate = (): void => {
    this.disableFileUploader = false;
    this.isDocumentTemplateNameValid = true;
    this.documentTemplateName = "";
    this.savedFiles = [];
    this.uploadedFiles = [];
    this.documentTemplateDetail = undefined;
    this.errorMessages = [];
    this.isDocumentTemplateSaveInProgress = false;
  }


}
